import { fetchUserSummaryCached } from 'features/salesOrder/userSummaryCache';

export const formatTransactionAmount = (amount: number, coinCode: string): string => {
	const formattedAmount = new Intl.NumberFormat('es-UY', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);

	return `${coinCode} ${formattedAmount}`;
};

/** Formato para pegar en Fiserv/call center: sin moneda ni separador de miles, decimales con coma */
export const formatAmountForCopy = (amount: number): string => {
	const normalized = Math.round(amount * 100) / 100;
	const [integerPart, decimalPart] = normalized.toFixed(2).split('.');
	return `${integerPart},${decimalPart}`;
};

export interface ITransactionAmountSummary {
	coinCode: string;
	totalAmount: number;
	totalAuthorized: number;
	pendingAuthorization: number;
	totalCrossed: number;
	pendingToCross: number;
	notAuthorizedForCross: number;
}

export const computeTransactionAmountSummary = (
	amount: number,
	pendingAuthorization: number,
	pendingToCross: number,
	coinCode: string,
): ITransactionAmountSummary => {
	const totalAuthorized = amount - pendingAuthorization;
	const totalCrossed = amount - pendingAuthorization - pendingToCross;

	return {
		coinCode,
		totalAmount: amount,
		totalAuthorized,
		pendingAuthorization,
		totalCrossed: Math.max(0, totalCrossed),
		pendingToCross,
		notAuthorizedForCross: pendingAuthorization,
	};
};

// Helper para copiar al portapapeles
export const copyToClipboard = async (text: string): Promise<boolean> => {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch (err) {
		console.error('Error al copiar al portapapeles:', err);
		return false;
	}
};

export const resolveSellerEmailByUsername = async (username: string): Promise<string | null> => {
	const users = await fetchUserSummaryCached();
	return users.find((user) => user.username === username)?.email ?? null;
};

// Determinar tipo de tarjeta desde el número
export const getCardType = (cardNumber: string): 'visa' | 'mastercard' | 'amex' | 'oca' | 'unknown' => {
	const cleaned = cardNumber.replace(/\s/g, '');

	// Visa: comienza con 4
	if (cleaned.startsWith('4')) return 'visa';

	// Mastercard: comienza con 51-55 o 2221-2720
	if (/^5[1-5]/.test(cleaned) || /^222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720/.test(cleaned)) return 'mastercard';

	// Amex: comienza con 34 o 37
	if (/^3[47]/.test(cleaned)) return 'amex';

	// OCA: comienza con 589657
	if (cleaned.startsWith('589657')) return 'oca';

	return 'unknown';
};

// Obtener gradiente según tipo de tarjeta
export const getCardGradient = (cardType: string): string => {
	const gradients = {
		visa: 'linear-gradient(135deg, #1A1F71 0%, #0066B2 100%)',
		mastercard: 'linear-gradient(135deg, #EB001B 0%, #F79E1B 100%)',
		amex: 'linear-gradient(135deg, #006FCF 0%, #00A3E0 100%)',
		oca: 'linear-gradient(135deg, #005EB8 0%, #00A859 100%)',
		unknown: 'linear-gradient(135deg, #424242 0%, #616161 100%)',
	};
	return gradients[cardType as keyof typeof gradients] || gradients.unknown;
};

// Formatear número de tarjeta con espacios
export const formatCardNumber = (cardNumber: string): string => {
	const cleaned = cardNumber.replace(/\s/g, '');
	return cleaned.replace(/(.{4})/g, '$1 ').trim();
};
